import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  signal,
  Type,
  ViewContainerRef,
} from '@angular/core';

/**
 * Internal tooltip element rendered by {@link LibTooltipDirective}. Defined as a
 * separate component so the host element doesn't accidentally receive the
 * tooltip's own host bindings (z-index stacking is the usual pitfall).
 *
 * <p>Pure Tailwind — fixed positioning relative to the directive's viewport
 * coordinates, dark-mode native, fade in/out via CSS opacity transition.
 * Pointer-events-none so the tooltip never steals clicks from underlying
 * controls. */
@Component({
  selector: 'lib-tooltip-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div
        class="pointer-events-none fixed z-[9999] max-w-xs rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg dark:bg-slate-700 dark:text-slate-100"
        [style.left.px]="x()"
        [style.top.px]="y()"
        role="tooltip">
        {{ text() }}
      </div>
    }
  `,
})
export class LibTooltipOverlayComponent {
  protected readonly text = signal('');
  protected readonly x = signal(0);
  protected readonly y = signal(0);
  protected readonly visible = signal(false);

  show(text: string, anchor: DOMRect, position: 'top' | 'bottom' | 'left' | 'right'): void {
    this.text.set(text);
    // 8px offset between the anchor and the tooltip — tight enough to read as
    // associated, loose enough that the tooltip isn't overlapping the focus
    // ring of the trigger.
    switch (position) {
      case 'top':
        this.x.set(anchor.left + anchor.width / 2);
        this.y.set(anchor.top - 8);
        break;
      case 'bottom':
        this.x.set(anchor.left + anchor.width / 2);
        this.y.set(anchor.bottom + 8);
        break;
      case 'left':
        this.x.set(anchor.left - 8);
        this.y.set(anchor.top + anchor.height / 2);
        break;
      case 'right':
        this.x.set(anchor.right + 8);
        this.y.set(anchor.top + anchor.height / 2);
        break;
    }
    this.visible.set(true);
  }

  hide(): void {
    this.visible.set(false);
  }
}

/**
 * V8 tooltip directive — drop-in replacement for {@code pTooltip}. Tailwind-
 * styled, dark-mode native, no PrimeNG dep. Mounts a portal-style overlay
 * into the application root view on hover/focus, removes it on leave/blur.
 *
 * <p>Usage:
 * <pre>
 *   <button [libTooltip]="'Manual position'">…</button>
 *   <button [libTooltip]="getRtkLabel()" libTooltipPosition="bottom">…</button>
 * </pre>
 *
 * <p>Why a portal rather than a sibling element: the tooltip needs to escape
 * any {@code overflow: hidden} ancestor (which is virtually every card we
 * have), and stacking against the FAB / dialogs requires a top-level layer.
 *
 * <p>Single overlay instance shared per directive — re-attaches on each
 * {@code mouseenter}, hides on {@code mouseleave}/{@code blur}/destroy.
 */
@Directive({
  selector: '[libTooltip]',
  standalone: true,
})
export class LibTooltipDirective implements OnDestroy {
  /** The tooltip text. Empty / nullish → directive becomes a no-op. */
  libTooltip = input<string | null | undefined>('');

  /** Where the tooltip sits relative to the host. Default {@code 'top'}. */
  libTooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  #host = inject(ElementRef<HTMLElement>);
  #vcr = inject(ViewContainerRef);
  #appRef = inject(ApplicationRef);
  #overlayRef: ComponentRef<LibTooltipOverlayComponent> | null = null;

  @HostListener('mouseenter')
  @HostListener('focus')
  onShow(): void {
    const text = (this.libTooltip() ?? '').trim();
    if (!text) return;
    this.ensureOverlay();
    const anchor = (this.#host.nativeElement as HTMLElement).getBoundingClientRect();
    this.#overlayRef!.instance.show(text, anchor, this.libTooltipPosition());
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onHide(): void {
    this.#overlayRef?.instance.hide();
  }

  ngOnDestroy(): void {
    if (this.#overlayRef) {
      this.#overlayRef.destroy();
      this.#overlayRef = null;
    }
  }

  /** Lazily create the overlay component and attach it to the application
   * root view so it sits in a portal-like layer above every other content. */
  private ensureOverlay(): void {
    if (this.#overlayRef) return;
    this.#overlayRef = this.#vcr.createComponent(LibTooltipOverlayComponent as Type<LibTooltipOverlayComponent>);
    // Append the rendered DOM node to body so it escapes any overflow/clip
    // ancestor. Angular still owns the lifecycle — it just lives in a
    // detached part of the document.
    document.body.appendChild(this.#overlayRef.location.nativeElement);
    // Re-attach the change detector so the overlay's signal-driven template
    // ticks even though it's been moved out of the host's view tree.
    this.#appRef.attachView(this.#overlayRef.hostView);
  }
}

/** Re-export for callers that import only the overlay (rare — usually you
 * just want the directive). */
export const _libTooltipExports = { LibTooltipOverlayComponent, LibTooltipDirective } as const;
// Suppress unused warning if computed isn't referenced — kept imported so
// future enhancements (e.g. computed positions) don't need a re-import.
void computed;
